import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './EquipmentList.module.scss';
import classNames from 'classnames/bind';
import { BaseImage, BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';

import MenuOption from '~/components/equipments/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { equipmentActions } from '~/thunks/equipment/equipmentSlice';
import { IEquipment, IEquipmentList } from '~/utils/interface/equipment';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type EquipmentListProps = {
  data?: IEquipment;
  onOpenEdit: (id: string) => void;
};

function EquipmentList(props: EquipmentListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/equipment']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('properties.name'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'image',
        label: t('properties.image'),
        sortable: false,
        searchable: false,
      },
      {
        id: '',
        label: '',
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );

  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { equipments, filter, currentPage, totalPages } = useAppSelector((state) => state.equipment);
  const dispatch = useAppDispatch();

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(equipmentActions.changeEquipmentId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(equipmentActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(equipmentActions.changeMenuSelectId(menuSelectedId));
      dispatch(equipmentActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      equipmentActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      equipmentActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  return (
    <Box className={cx('equipment-list-container')}>
      <BaseTable<IEquipmentList>
        data={equipments}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(equipmentItem: IEquipmentList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('equipment-container')}>
                  <Box className={cx('equipment-name-container')}>
                    <Box className={cx('equipmentName')}>{equipmentItem.name}</Box>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={cx('equipment-container')}>
                  <Box className={cx('equipment-name-container')}>
                    {equipmentItem.image && <BaseImage src={equipmentItem.image} className={cx('image')} />}
                  </Box>
                </Box>
              </TableCell>
              {(permissions[PERMISSIONS.equipment_delete] ||
                permissions[PERMISSIONS.equipment_edit] ||
                permissions[PERMISSIONS.equipment_view]) && (
                <TableCell>
                  <Box className={'flex justify-end'}>
                    <Box className={cx('equipment-container')}>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenu(event, equipmentItem.id);
                        }}
                      >
                        <MenuVerticalIcon className='w-[19px] h-[19px]' />
                      </IconButton>
                    </Box>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(EquipmentList);
